import Link from 'next/link'
import { useRouter } from 'next/router'
import { useMemo } from 'react'

import type { Language, SupportedMarketKey } from '@nxt-insurance/constants'
import UiKitLanguageSwitcher from '@nxt-insurance/ui-kit/components/LanguageSwitcher'

import { EVENTS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import type { AlternateLink } from '@/hooks/useAlternateLinks'
import { reportErrorMessage } from '@/services/errorReporting'
import { getLanguagesForMarket, getLocaleParts } from '@/utils/locale'
import { getPathWithParams, removeImpermanentSearchParams } from '@/utils/url'

interface Props {
  onPress?: () => void
  alternateLinks?: AlternateLink[]
  removeQueryParams?: boolean
}

export const useGetNewLocaleFromSwitcher = (
  market: SupportedMarketKey,
  locales: readonly string[] | undefined,
  language: Language
) => {
  const languages = useMemo(() => {
    return getLanguagesForMarket(locales as string[], market)
  }, [market, locales])

  const otherLanguage = languages.find((selectLang) => selectLang !== language)
  const newLocale = `${otherLanguage}-${market}`

  return { newLocale, languages, otherLanguage }
}

export const LanguageSwitcher = ({ onPress, alternateLinks, removeQueryParams }: Props) => {
  const { query, locale, locales } = useRouter()
  const [lang, market] = getLocaleParts(locale as string)
  const { track } = useAnalytics()

  const whitelistedParams = useMemo(() => removeImpermanentSearchParams(query).toString(), [query])

  const { newLocale, languages, otherLanguage } = useGetNewLocaleFromSwitcher(market, locales, lang)

  const href = alternateLinks?.find((link) => link.hrefLang === newLocale)?.href.split(newLocale)[1]

  if (languages.length > 2) {
    reportErrorMessage("LanguageSwitcher doesn't support more than 2 languages at the moment")
  }

  if (languages.length === 1) {
    return null
  }

  return (
    <Link
      href={getPathWithParams(href ?? '/', { params: removeQueryParams ? '' : whitelistedParams })}
      locale={newLocale}
      data-testid="CY_langSwitcher"
      aria-label={otherLanguage}
      onClick={() => {
        onPress?.()
        track(EVENTS.LanguageSwitch, { language: otherLanguage })
      }}
    >
      <UiKitLanguageSwitcher lang={lang} availableLanguages={languages} />
    </Link>
  )
}
